import React, { useState, useEffect } from 'react';
import { Shield, X, Save, Check, Layers, AlertCircle, CheckCircle2 } from 'lucide-react';
import { GroupWithPrograms, SystemProgram, userManagementService } from '../services/userManagementService';

interface Props {
  group: GroupWithPrograms | null;
  isOpen: boolean;
  onClose: () => void;
  allPrograms: SystemProgram[];
  onSaved: () => void;
}

export const EditarGrupoModal: React.FC<Props> = ({
  group,
  isOpen,
  onClose,
  allPrograms,
  onSaved
}) => {
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>(group?.program_ids || []);
  const [salvando, setSalvando] = useState(false);
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    if (group) {
      setSelectedProgramIds(group.program_ids || []);
    }
  }, [group]);

  if (!isOpen || !group) return null;

  const handleToggleProgram = (id: number) => {
    if (selectedProgramIds.includes(id)) {
      setSelectedProgramIds(selectedProgramIds.filter(p => p !== id));
    } else {
      setSelectedProgramIds([...selectedProgramIds, id]);
    }
  };

  const handleSelectAll = () => {
    setSelectedProgramIds(allPrograms.map(p => p.id));
  };

  const handleClearAll = () => {
    setSelectedProgramIds([]);
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const ok = await userManagementService.salvarProgramasDoGrupo(group.id, selectedProgramIds);
      if (ok) {
        setSucesso(true);
        setTimeout(() => {
          setSucesso(false);
          onSaved();
          onClose();
        }, 1000);
      }
    } catch (e) {
      console.error('Erro ao salvar programas do grupo:', e);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl w-full max-w-3xl max-h-[94vh] flex flex-col shadow-2xl overflow-hidden text-left my-auto">
        
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-start sm:items-center justify-between gap-3 bg-slate-950/60 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30 shrink-0">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg font-black text-white flex flex-wrap items-center gap-2">
                <span>Editar Programas do Grupo</span>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 border border-indigo-800 shrink-0">
                  {group.name} (#{group.id})
                </span>
              </h2>
              <p className="text-xs text-slate-400 truncate sm:whitespace-normal">
                Selecione os módulos e rotas liberados na tabela <code className="text-indigo-300">system_group_program</code>
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition shrink-0 cursor-pointer"
            title="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Notificação de Sucesso */}
        {sucesso && (
          <div className="mx-4 sm:mx-6 mt-3 sm:mt-4 p-3 rounded-2xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2 shrink-0 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Programas do grupo atualizados com sucesso no Supabase!</span>
          </div>
        )}

        {/* Lista de Programas */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 flex-1 min-h-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-2.5">
            <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
              {selectedProgramIds.length} de {allPrograms.length} programas liberados
            </span>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs font-bold text-indigo-400 hover:text-indigo-300 cursor-pointer"
              >
                Liberar Todos
              </button>
              <span className="text-slate-600">|</span>
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs font-bold text-slate-400 hover:text-slate-300 cursor-pointer"
              >
                Desmarcar Todos
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            {allPrograms.map((prog) => {
              const isSelected = selectedProgramIds.includes(prog.id);
              return (
                <label
                  key={prog.id}
                  className={`flex items-start gap-3 p-3 sm:p-3.5 rounded-2xl border cursor-pointer transition select-none min-w-0 ${
                    isSelected
                      ? 'bg-indigo-950/40 border-indigo-600/70 text-indigo-200 shadow-sm'
                      : 'bg-slate-950/60 border-slate-800/80 text-slate-400 hover:border-slate-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleProgram(prog.id)}
                    className="mt-0.5 rounded border-slate-700 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold leading-tight text-white truncate">{prog.name}</div>
                    <div className="text-[10px] font-mono text-indigo-400/80 mt-0.5 truncate">
                      {prog.controller}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3.5 sm:p-5 md:p-6 border-t border-slate-800 bg-slate-950 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 text-xs font-bold transition cursor-pointer text-center"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSalvar}
            disabled={salvando}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 disabled:opacity-50 cursor-pointer"
          >
            <Save className="w-4 h-4 shrink-0" />
            <span>{salvando ? 'Salvando...' : 'Salvar Programas do Grupo'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};
